/**
 * useJudgmentLaw — thin data hooks that talk to the Phase 9A judgment-law engine.
 *
 *  - useStateVariants()  → { options, byKey, loading, error }
 *      Loads GET /api/judgment-law/states once and returns dropdown-ready
 *      options for the JeaSelect component (label formatted with the
 *      Phase 9A variant display name, e.g. "California",
 *      "Colorado — District Court", "Arizona — On or after Aug 3 2013").
 *
 *  - useJudgmentTimeline(payload) → { data, loading, error, refresh }
 *      Debounced POST /api/judgment-law/timeline whenever `state` +
 *      `date_entered` are both present. Returns null while incomplete or
 *      loading — consumers should render skeletons / hint copy.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";

// Variant → user-friendly label overrides (Phase 9B UX polish). We prefer
// clear year/court-name wording over the raw API `name` for multi-variant
// states so the dropdown reads like a form label, not a data dump.
const LABEL_OVERRIDES = {
  AZ_POST_2013: "Arizona — On or after Aug 3, 2013",
  AZ_PRE_2013_UNRENEWED: "Arizona — Before Aug 3, 2013 (unrenewed by Aug 2, 2018)",
  CO_DISTRICT: "Colorado — District Court",
  CO_COUNTY: "Colorado — County Court",
  CT_SUPERIOR: "Connecticut — Superior Court",
  CT_SMALL_CLAIMS: "Connecticut — Small Claims",
  VA_PRE_2021: "Virginia — Before July 1, 2021",
  VA_POST_2021: "Virginia — On or after July 1, 2021",
};

const _cache = { promise: null, states: null };

export const useStateVariants = () => {
  const [state, setState] = useState({ options: [], byKey: {}, loading: true, error: null });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (!_cache.states) {
          _cache.promise = _cache.promise || apiClient.get("/judgment-law/states").then((r) => {
            _cache.states = r.data?.states || [];
            return _cache.states;
          });
          await _cache.promise;
        }
        const states = _cache.states || [];
        // Sort by display label alphabetically for a clean dropdown.
        const options = states
          .map((s) => ({
            value: s.variant_key,
            label: LABEL_OVERRIDES[s.variant_key] || s.name,
            state_code: s.state_code,
            description: s.duration_note,
            hard_cutoff: s.hard_cutoff,
            expired: s.expired,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        const byKey = Object.fromEntries(states.map((s) => [s.variant_key, s]));
        if (mounted) setState({ options, byKey, loading: false, error: null });
      } catch (e) {
        if (mounted) setState({ options: [], byKey: {}, loading: false, error: e });
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  return state;
};

// -------------------------------------------------------------------------

export const useJudgmentTimeline = (payload) => {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const timerRef = useRef(null);

  const ready = !!(payload?.state && payload?.date_entered);

  const fetch = useCallback(async () => {
    if (!ready) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await apiClient.post("/judgment-law/timeline", payload);
      setState({ data, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e });
    }
  }, [ready, JSON.stringify(payload)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ready) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fetch, 250);
    return () => clearTimeout(timerRef.current);
  }, [fetch, ready]);

  return { ...state, refresh: fetch };
};

export const variantToStateCode = (variantKey) => {
  if (!variantKey) return "";
  // Data keys are either a bare 2-letter code (e.g. "CA") or a suffixed
  // variant key (e.g. "AZ_POST_2013"). The first 2 chars are always the state.
  return variantKey.slice(0, 2).toUpperCase();
};

export const variantToCourtLevelHint = (variantKey) => {
  if (!variantKey) return null;
  const map = {
    AZ_POST_2013: "post_2013",
    AZ_PRE_2013_UNRENEWED: "pre_2013_unrenewed",
    CO_DISTRICT: "district",
    CO_COUNTY: "county",
    CT_SUPERIOR: "superior",
    CT_SMALL_CLAIMS: "small_claims",
    VA_PRE_2021: "pre_2021",
    VA_POST_2021: "post_2021",
  };
  return map[variantKey] || null;
};
